import { Component, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { AdmytService } from '../../../services/admyt.service'

interface AdmytTenant {
    id: string
    name: string
    centreId: string
    centreName?: string
    status: string
    baysNumber: number
    createdAt?: string
    updatedAt?: string
}

interface AdmytCenter {
    id: string
    name: string
}

@Component({
    selector: 'app-admin-list-admyt-tenants',
    templateUrl: './admin-list-admyt-tenants.component.html',
    styleUrls: ['./admin-list-admyt-tenants.component.scss'],
})
export class AdminListAdmytTenantsComponent implements OnInit {
    tenants: AdmytTenant[] = []
    filteredTenants: AdmytTenant[] = []
    centers: AdmytCenter[] = []
    loading = false
    loadingCenters = true
    loadingTenants = false
    error: string | null = null
    Math = Math

    // Selected center (required before loading tenants)
    selectedCenterId: string = ''
    selectedCenter: AdmytCenter | null = null

    // Pagination
    currentPage = 1
    itemsPerPage = 20
    totalItems = 0
    totalPages = 0

    // Filtering
    searchTerm = ''
    statusFilter = 'all'
    availableStatuses = ['all', 'active', 'inactive', 'pending']

    constructor(private router: Router, private admytService: AdmytService) {}

    async ngOnInit() {
        await this.loadCenters()
    }

    async loadCenters() {
        try {
            this.loadingCenters = true
            this.error = null

            const centersResult = await this.admytService.getCentres()
            this.centers = (centersResult.data as any) || []
            console.log('Centers loaded:', this.centers)

            this.loadingCenters = false
        } catch (error) {
            console.error('Failed to load centers:', error)
            this.error = 'Failed to load centers. Please try again.'
            this.loadingCenters = false
        }
    }

    async onCenterChange() {
        if (!this.selectedCenterId) {
            this.tenants = []
            this.filteredTenants = []
            this.selectedCenter = null
            return
        }

        this.selectedCenter = this.centers.find(c => String(c.id) === String(this.selectedCenterId)) || null
        await this.loadTenantsForCenter(this.selectedCenterId)
    }

    async loadTenantsForCenter(centerId: string) {
        try {
            this.loadingTenants = true
            this.error = null

            const centerName = this.selectedCenter?.name || centerId
            console.log(`Fetching tenants for center: ${centerId} (${centerName})`)

            const tenantsResult = await this.admytService.getTenants(centerId, 200, 0, 'all')
            const tenantsData = tenantsResult.data as any
            
            this.tenants = (tenantsData.data || []).map((tenant: any) => ({
                ...tenant,
                centreId: centerId,
                centreName: centerName,
            }))

            console.log(`Successfully loaded ${this.tenants.length} tenants for center ${centerId}`)
            
            this.totalItems = this.tenants.length
            this.applyFilters()

            this.loadingTenants = false
        } catch (error) {
            console.error(`Failed to load tenants for center ${centerId}:`, error)
            this.error = `Failed to load tenants for this center. Please try again.`
            this.tenants = []
            this.filteredTenants = []
            this.loadingTenants = false
        }
    }

    async refreshData() {
        if (this.selectedCenterId) {
            await this.loadTenantsForCenter(this.selectedCenterId)
        }
    }

    applyFilters() {
        let filtered = [...this.tenants]

        // Apply search filter
        if (this.searchTerm.trim()) {
            const searchLower = this.searchTerm.toLowerCase()
            filtered = filtered.filter(
                (tenant) =>
                    tenant.name.toLowerCase().includes(searchLower) || 
                    String(tenant.id).toLowerCase().includes(searchLower)
            )
        }

        // Apply status filter
        if (this.statusFilter !== 'all') {
            filtered = filtered.filter((tenant) => tenant.status === this.statusFilter)
        }

        this.filteredTenants = filtered
        this.totalItems = filtered.length
        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage)

        // Reset to first page if current page is beyond available pages
        if (this.currentPage > this.totalPages && this.totalPages > 0) {
            this.currentPage = 1
        }
    }

    onSearchChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    onStatusFilterChange() {
        this.currentPage = 1
        this.applyFilters()
    }

    getPaginatedTenants(): AdmytTenant[] {
        const startIndex = (this.currentPage - 1) * this.itemsPerPage
        const endIndex = startIndex + this.itemsPerPage
        return this.filteredTenants.slice(startIndex, endIndex)
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages) {
            this.currentPage = page
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = []
        const maxPagesToShow = 5
        const halfRange = Math.floor(maxPagesToShow / 2)

        let startPage = Math.max(1, this.currentPage - halfRange)
        let endPage = Math.min(this.totalPages, this.currentPage + halfRange)

        // Adjust if we're near the beginning or end
        if (endPage - startPage + 1 < maxPagesToShow) {
            if (startPage === 1) {
                endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1)
            } else if (endPage === this.totalPages) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1)
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i)
        }

        return pages
    }

    viewTenant(tenant: AdmytTenant) {
        this.router.navigate(['/admin/admyt-center', tenant.centreId, 'tenant', tenant.id])
    }

    getStatusBadgeClass(status: string): string {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'badge bg-success'
            case 'inactive':
                return 'badge bg-secondary'
            case 'pending':
                return 'badge bg-warning text-dark'
            default:
                return 'badge bg-light text-dark'
        }
    }

    trackByTenantId(index: number, tenant: AdmytTenant): string {
        return tenant.id
    }

    async deactivateTenant(tenant: AdmytTenant) {
        const confirmMessage = `Are you sure you want to deactivate tenant "${tenant.name}" (ID: ${tenant.id}) from ${tenant.centreName}?\n\nThis action cannot be undone.`

        if (!confirm(confirmMessage)) {
            return
        }

        try {
            console.log(`Deactivating tenant ${tenant.id} from center ${tenant.centreId}`)

            await this.admytService.deactivateTenant(tenant.centreId, tenant.id)

            console.log(`Successfully deactivated tenant ${tenant.id}`)

            // Remove the tenant from the local list
            this.tenants = this.tenants.filter((t) => !(t.id === tenant.id && t.centreId === tenant.centreId))
            this.applyFilters()

            alert(`Tenant "${tenant.name}" has been successfully deactivated.`)
        } catch (error) {
            console.error('Failed to deactivate tenant:', error)
            alert(`Failed to deactivate tenant "${tenant.name}". Please try again.\n\nError: ${error}`)
        }
    }

    // Stats computed properties
    getActiveTenants(): number {
        return this.tenants.filter(tenant => tenant.status === 'active').length
    }

    getTotalBays(): number {
        return this.tenants.reduce((total, tenant) => total + (tenant.baysNumber || 0), 0)
    }

    /**
     * Parse subscription ID from tenant name
     * Tenant names follow pattern: "{FirstName} {LastName} {subscriptionId}"
     */
    getSubscriptionFromTenantName(name: string): string | null {
        if (!name) return null
        const parts = name.split(' ')
        if (parts.length >= 3) {
            return parts[parts.length - 1]
        }
        return null
    }

    /**
     * Truncate any value (string or number) to specified length
     */
    truncate(value: any, maxLength: number): string {
        if (value === null || value === undefined) return ''
        const str = String(value)
        if (str.length <= maxLength) return str
        return str.substring(0, maxLength) + '...'
    }
}
